// ─────────────────────────────────────────────────────────────────
// Glass-i — Inventory Current Stock LIST page (Stage 6P.5).
//
// Server-paginated list at /erp/inventory/stock.
//
// Columns (brief):
//   Item Type (Tag via itemTypeColor) · Stock Name · Thickness ·
//   W · H · Qty Balance · SQMT Balance · SQFT Balance · Length ·
//   Total Length Balance · UOM · Warehouse · Rack · Batch/Lot ·
//   Last Movement · Actions (⋯ menu: View ledger).
//
// Toolbar:
//   Search (q over item_name / stock_name) · Item type filter ·
//   Warehouse filter (derived from loaded rows) · Thickness filter ·
//   Reset / Refresh.
//
// Pagination: 25 / 50 / 100 (offset + limit translated from
// page + pageSize because the backend exposes offset/limit, not
// page/pageSize, on inventoryStock.service.listCurrentStock).
//
// ADDITIVE-ONLY: this page reads `inventoryApi.listStock` + the
// shared inventory helpers (`itemTypeLabel`, `itemTypeColor`,
// `postingStatusColor`, `ITEM_TYPES`, `normalizeCurrentStockRow`)
// from `./inventoryHelpers`. It does NOT mutate any inventory row —
// the inward post / ledger surfaces own that responsibility.
//
// Mirrors the compact enterprise AntD list pattern from
// PurchaseBillsPage:
//   • slate-50 header strip + uppercase column titles
//   • 22 px row density (we reuse the .grn-* CSS scoped under
//     a dedicated .grn-shell wrapper class so the inventory grid
//     inherits the same chrome as GRN / PO / Bill grids).
//   • sticky toolbar
//   • ⋯ row-actions menu
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, Dropdown, Input, InputNumber, Segmented, Select,
  Space, Spin, Table, Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, MoreOutlined,
  AppstoreOutlined, EyeOutlined, HistoryOutlined,
  UnorderedListOutlined, TableOutlined
} from '@ant-design/icons';

import { inventoryApi } from './inventoryApi';
import {
  ITEM_TYPES,
  itemTypeLabel,
  itemTypeColor,
  normalizeCurrentStockRow
} from './inventoryHelpers';
import StockPivotView from './StockPivotView';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const SEARCH_DEBOUNCE_MS = 250;

const ITEM_TYPE_OPTIONS = ITEM_TYPES.map((v) => ({
  value: v,
  label: itemTypeLabel(v)
}));

function fmtDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  const hh    = String(d.getHours()).padStart(2, '0');
  const mm    = String(d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hh}:${mm}`;
}

// Numeric cell formatter — `dp` decimal places, en-IN grouping.
// Falsy / non-finite inputs collapse to an em-dash so empty rows
// don't read as "0.00".
function fmtNumber(value, dp) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp
    });
  } catch {
    return n.toFixed(dp);
  }
}

// Compact mm display — drops trailing zeros, suffixes "mm".
function fmtMm(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  const s = Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
  return `${s}`;
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// Batch / Lot cell — collapse to em-dash if both empty; show both
// stacked when present.
function batchLotCell(row) {
  const batch = pickField(row, 'batch_no', 'batchNo');
  const lot   = pickField(row, 'lot_no',   'lotNo');
  if (!batch && !lot) return <span className="grn-muted">—</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
      {batch ? <span className="grn-mono">{batch}</span> : null}
      {lot   ? <span className="grn-muted" style={{ fontSize: 10.5 }}>{lot}</span> : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function CurrentStockPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  // Wildcard '*' is the master role; explicit perm strings cover
  // admin/manager/staff/viewer per userPermissions.js.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView   = hasPerm('erp.inventory.stock.view');
  const canLedger = hasPerm('erp.inventory.stock_ledger.view');

  // ── View mode (flat List ↔ aggregate Pivot) ──────────────────
  const [viewMode, setViewMode] = useState('list');

  // ── Toolbar state ────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [search,      setSearch]      = useState('');
  const [itemType,    setItemType]    = useState(null);
  const [warehouseId, setWarehouseId] = useState(null);
  const [thickness,   setThickness]   = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  // Warehouse options are derived from the rows we've already
  // loaded so the operator gets a useful dropdown even though the
  // backend does not yet expose a warehouse master endpoint.
  const [knownWarehouses, setKnownWarehouses] = useState(() => new Map());

  // ── Debounced search wiring ──────────────────────────────────
  // Keep the visible input snappy, but only re-fetch once the
  // operator pauses typing — same pattern as the bills list.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // ── Load ─────────────────────────────────────────────────────
  // The flat list only fetches while its view is active — the Pivot view
  // owns its own server-aggregated fetch, so we don't pay for both.
  const load = useCallback(async () => {
    if (!canView || viewMode !== 'list') return;
    setLoading(true);
    try {
      // Backend uses offset/limit, not page/pageSize. Translate.
      const limit  = pageSize;
      const offset = (page - 1) * pageSize;
      const params = { limit, offset };

      const needle = (search || '').trim();
      if (needle) params.search = needle;
      if (itemType)    params.item_type    = itemType;
      if (warehouseId) params.warehouse_id = warehouseId;
      if (thickness !== null && thickness !== undefined && thickness !== '') {
        const n = Number(thickness);
        if (Number.isFinite(n) && n > 0) params.thickness_mm = n;
      }

      const out  = await inventoryApi.listStock(params);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : [];
      const count = Array.isArray(out) ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      const normalized = list
        .map((r) => normalizeCurrentStockRow(r))
        .filter(Boolean);

      // Harvest warehouse names → ids so the warehouse filter
      // dropdown gets useful options. Merge into existing set so we
      // don't lose options when filtering narrows the visible page.
      setKnownWarehouses((prev) => {
        const next = new Map(prev);
        for (const r of normalized) {
          const id   = r.warehouseId;
          const name = r.warehouseName;
          if (id && !next.has(id)) {
            next.set(id, name || id);
          }
        }
        return next;
      });

      setRows(normalized);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load current stock.'),
        key: 'inv-stock-list'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, viewMode, page, pageSize, search, itemType, warehouseId, thickness]);

  useEffect(() => { load(); }, [load]);

  // Shared filter surface handed to the Pivot view — same semantics as
  // the list query so the two views always agree on which rows are in scope.
  const pivotFilters = useMemo(() => ({
    search:       (search || '').trim() || undefined,
    item_type:    itemType || undefined,
    warehouse_id: warehouseId || undefined,
    thickness_mm: (thickness !== null && thickness !== undefined && thickness !== '')
      ? thickness : undefined
  }), [search, itemType, warehouseId, thickness]);

  // ── Toolbar handlers — any filter change resets page to 1 ────
  const onItemType    = (v) => { setItemType(v || null);    setPage(1); };
  const onWarehouse   = (v) => { setWarehouseId(v || null); setPage(1); };
  const onThickness   = (v) => { setThickness(v == null ? null : v); setPage(1); };
  const onReset       = () => {
    setSearchInput('');
    setSearch('');
    setItemType(null);
    setWarehouseId(null);
    setThickness(null);
    setPage(1);
  };

  // ── Row navigation ───────────────────────────────────────────
  // Stock ledger is the natural drill-through from current stock.
  // Pre-filter by item so the operator lands on a focused ledger.
  const openLedgerForItem = useCallback((row) => {
    const itemId = pickField(row, 'item_id', 'itemId');
    const url    = itemId
      ? `/erp/inventory/stock-ledger?itemId=${encodeURIComponent(itemId)}`
      : '/erp/inventory/stock-ledger';
    navigate(url);
  }, [navigate]);

  // "View" on a current-stock row is read-only — we route to the
  // stock-ledger drill-through too, since current-stock has no
  // dedicated detail page in Stage 6P.5.
  const openView = useCallback((row) => {
    openLedgerForItem(row);
  }, [openLedgerForItem]);

  // ── Row-action menu builder ──────────────────────────────────
  const buildRowMenu = useCallback((row) => {
    const items = [];
    items.push({
      key:   'view',
      icon:  <EyeOutlined />,
      label: 'View'
    });
    if (canLedger) {
      items.push({
        key:   'ledger',
        icon:  <HistoryOutlined />,
        label: 'Stock Ledger'
      });
    }
    return items;
  }, [canLedger]);

  const onRowMenu = useCallback((key, row) => {
    switch (key) {
      case 'view':   return openView(row);
      case 'ledger': return openLedgerForItem(row);
      default:       return undefined;
    }
  }, [openView, openLedgerForItem]);

  // Warehouse dropdown options derived from rows we've seen.
  const warehouseOptions = useMemo(() => {
    const arr = [];
    for (const [id, name] of knownWarehouses.entries()) {
      arr.push({ value: id, label: name });
    }
    arr.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    return arr;
  }, [knownWarehouses]);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Item Type',
      dataIndex: 'itemType',
      width:     110,
      render: (_v, row) => {
        const t = pickField(row, 'item_type', 'itemType');
        if (!t) return <span className="grn-muted">—</span>;
        return (
          <Tag color={itemTypeColor(t)} className="grn-status-tag">
            {itemTypeLabel(t)}
          </Tag>
        );
      }
    },
    {
      title:     'Stock Name',
      dataIndex: 'stockName',
      render: (_v, row) => {
        const stockName = pickField(row, 'stock_name', 'stockName');
        const itemName  = pickField(row, 'item_name',  'itemName');
        const primary   = stockName || itemName || '—';
        const secondary = stockName && itemName && stockName !== itemName
          ? itemName
          : null;
        return (
          <div className="grn-supplier">
            <Tooltip title={primary}>
              <span className="grn-supplier__name">{primary}</span>
            </Tooltip>
            {secondary
              ? <span className="grn-muted" style={{ fontSize: 10.5 }}>{secondary}</span>
              : null}
          </div>
        );
      }
    },
    {
      title:     'Thickness',
      dataIndex: 'thicknessMm',
      width:     90,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtMm(pickField(row, 'thickness_mm', 'thicknessMm'))}
        </span>
      )
    },
    {
      title:     'W',
      dataIndex: 'widthMm',
      width:     70,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtMm(pickField(row, 'width_mm', 'widthMm'))}
        </span>
      )
    },
    {
      title:     'H',
      dataIndex: 'heightMm',
      width:     70,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtMm(pickField(row, 'height_mm', 'heightMm'))}
        </span>
      )
    },
    {
      title:     'Qty Balance',
      dataIndex: 'qtyBalance',
      width:     110,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtNumber(pickField(row, 'qty_balance', 'qtyBalance'), 4)}
        </span>
      )
    },
    {
      title:     'SQMT Balance',
      dataIndex: 'sqmtBalance',
      width:     120,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtNumber(pickField(row, 'sqmt_balance', 'sqmtBalance'), 3)}
        </span>
      )
    },
    {
      title:     'SQFT Balance',
      dataIndex: 'sqftBalance',
      width:     120,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtNumber(pickField(row, 'sqft_balance', 'sqftBalance'), 3)}
        </span>
      )
    },
    {
      title:     'Length',
      dataIndex: 'lengthValue',
      width:     90,
      align:     'right',
      render: (_v, row) => {
        const v   = pickField(row, 'length_value', 'lengthValue');
        const uom = pickField(row, 'length_uom',   'lengthUom');
        if (v === null || v === undefined || v === '' || Number(v) <= 0) {
          return <span className="grn-muted">—</span>;
        }
        return (
          <span className="grn-mono">
            {fmtNumber(v, 4)}
            {uom ? <span className="grn-muted" style={{ marginLeft: 4 }}>{uom}</span> : null}
          </span>
        );
      }
    },
    {
      title:     'Total Length Balance',
      dataIndex: 'totalLengthBalance',
      width:     150,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtNumber(pickField(row, 'total_length_balance', 'totalLengthBalance'), 4)}
        </span>
      )
    },
    {
      title:     'UOM',
      dataIndex: 'uom',
      width:     80,
      render: (_v, row) => {
        const uom = pickField(row, 'uom', 'uom');
        if (!uom) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{uom}</span>;
      }
    },
    {
      title:     'Warehouse',
      dataIndex: 'warehouseName',
      width:     150,
      render: (_v, row) => {
        const name = pickField(row, 'warehouse_name', 'warehouseName');
        if (!name) return <span className="grn-muted">—</span>;
        return (
          <Tooltip title={name}>
            <span className="grn-supplier__name">{name}</span>
          </Tooltip>
        );
      }
    },
    {
      title:     'Rack',
      dataIndex: 'rackName',
      width:     110,
      render: (_v, row) => {
        const name = pickField(row, 'rack_name', 'rackName');
        if (!name) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{name}</span>;
      }
    },
    {
      title:     'Batch / Lot',
      dataIndex: 'batchLot',
      width:     130,
      render: (_v, row) => batchLotCell(row)
    },
    {
      title:     'Last Movement',
      dataIndex: 'lastMovementAt',
      width:     150,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDateTime(pickField(row, 'last_movement_at', 'lastMovementAt'))}
        </span>
      )
    },
    {
      title:     '',
      key:       '_actions',
      width:     56,
      fixed:     'right',
      align:     'center',
      render: (_v, row) => {
        const items = buildRowMenu(row);
        return (
          <Dropdown
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                if (domEvent && domEvent.stopPropagation) domEvent.stopPropagation();
                onRowMenu(key, row);
              }
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              className="grn-actions-btn"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
              aria-label="Row actions"
            />
          </Dropdown>
        );
      }
    }
  ]), [buildRowMenu, onRowMenu]);

  // ── Render ───────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view inventory stock."
          description="Ask your master account to grant erp.inventory.stock.view."
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><AppstoreOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">Current Stock</span>
            <span className="grn-headstrip__subtitle">
              Live balances by item, thickness, warehouse, rack and batch/lot.
              Backend remains the authority for posted qty, sqmt/sqft, total
              length and last-movement bookkeeping — this view is read-only.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> total
          </span>
        </Space>
      </div>

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search item / stock name…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={(e) => {
            // Flush debounce on Enter.
            setSearchInput(e.target.value);
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 280 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All item types"
          value={itemType || undefined}
          onChange={onItemType}
          options={ITEM_TYPE_OPTIONS}
          style={{ width: 160 }}
        />
        <Select
          allowClear
          showSearch
          size="small"
          placeholder="All warehouses"
          value={warehouseId || undefined}
          onChange={onWarehouse}
          options={warehouseOptions}
          optionFilterProp="label"
          style={{ width: 200 }}
          notFoundContent={
            warehouseOptions.length === 0
              ? <span style={{ fontSize: 11.5, color: '#94a3b8' }}>
                  No warehouses on screen
                </span>
              : null
          }
        />
        <InputNumber
          size="small"
          placeholder="Thickness (mm)"
          value={thickness}
          onChange={onThickness}
          min={0}
          step={0.5}
          style={{ width: 140 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Segmented
          size="small"
          value={viewMode}
          onChange={(v) => setViewMode(v)}
          options={[
            { value: 'list',  icon: <UnorderedListOutlined />, label: 'List' },
            { value: 'pivot', icon: <TableOutlined />,         label: 'Pivot' }
          ]}
        />
        {viewMode === 'list' ? (
          <Tooltip title="Refresh">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={load}
            />
          </Tooltip>
        ) : null}
      </div>

      {/* Pivot view — item × sheet size aggregate (server-side) */}
      {viewMode === 'pivot' ? (
        <StockPivotView filters={pivotFilters} />
      ) : (
      /* Grid card */
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) =>
              pickField(r, 'id', 'id')
              || `${pickField(r, 'item_id', 'itemId') || ''}-${pickField(r, 'warehouse_id', 'warehouseId') || ''}-${pickField(r, 'thickness_mm', 'thicknessMm') || ''}-${pickField(r, 'batch_no', 'batchNo') || ''}-${pickField(r, 'lot_no', 'lotNo') || ''}`
            }
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            onRow={(row) => ({
              onDoubleClick: () => openView(row)
            })}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No stock on hand yet — post a GRN or Purchase Bill from Inward Posting to create balances.'
            }}
          />
        </Spin>
      </Card>
      )}
    </div>
  );
}
